import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  FaEnvelope,
  FaLock,
  FaUserPlus,
  FaUser,
  FaCheckCircle,
  FaTimesCircle,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";
import { useToastHelpers } from "../Toasts/ToastUtils";
import { useAuth } from "./AuthContext";
import { registerUser } from "../../apis/UserApis";
import SeoMeta from "../SeoMeta";
import { useTheme } from "../Theme/ThemeContext";

export default function SignUpPage() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { successToast, errorToast } = useToastHelpers();
  const { user } = useAuth();
  const { currentTheme } = useTheme();
  const role = "user";

  // ✅ Password validation rules
  const passwordRules = {
    upperCase: /[A-Z]/.test(password),
    lowerCase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    minLength: password.length >= 8,
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!firstName || !lastName || !email || !password || !confirmPassword) {
      setError("⚠️ Please fill in all fields.");
      return;
    }

    if (password !== confirmPassword) {
      setError("⚠️ Password and Confirm Password do not match.");
      return;
    }

    if (!Object.values(passwordRules).every(Boolean)) {
      setError("⚠️ Password must meet all requirements.");
      return;
    }

    setError("");

    registerUser({ firstName, lastName, role, email, password })
      .then(() => {
        successToast(
          "Sign-up successful",
          "Your account has been created! Please login."
        );
        window.location.href = "/login";
      })
      .catch((err) => {
        console.error("Sign-up failed:", err);
        errorToast(
          "Sign-up failed. Please try again.",
          err?.response?.data?.message || "Unknown error"
        );
      });
  };

  if (user && user.token) {
    return <Navigate to="/" />;
  }

  return (
    <>
      {/* SEO Meta */}
      <SeoMeta
        title="Sign Up | CodeArena"
        description="Create a new account on CodeArena to access courses and job opportunities."
        url="https://www.code-arena.in/signup"
        image="/logo192.png"
        keywords="CodeArena, Sign Up, Job Portal, Courses, React"
        type="website"
      />

      <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-neutral-50 via-white to-accent-50/30 dark:from-neutral-900 dark:via-neutral-900 dark:to-neutral-800 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-accent-200/40 dark:bg-accent-900/20 rounded-full filter blur-3xl opacity-40 -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-emerald-200/40 dark:bg-emerald-900/20 rounded-full filter blur-3xl opacity-40 translate-x-1/2 translate-y-1/2"></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-royal-200/30 dark:bg-royal-900/20 rounded-full filter blur-3xl opacity-30 -translate-x-1/2 -translate-y-1/2"></div>
        
        <div className="relative max-w-md w-full">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className={`w-12 h-12 bg-gradient-to-br ${currentTheme.colors.gradient} rounded-2xl flex items-center justify-center shadow-lg ${currentTheme.colors.glow}`}>
                <span className="text-white font-bold text-2xl">C</span>
              </div>
              <span className={`text-2xl font-bold bg-gradient-to-r ${currentTheme.colors.gradient} bg-clip-text text-transparent`}>
                CodeArena
              </span>
            </Link>
          </div>
          
          <div className="glass rounded-3xl shadow-xl border border-white/20 dark:border-white/10 p-8 sm:p-10">
            {/* Title */}
            <h2 className="text-3xl font-bold text-neutral-900 dark:text-white text-center mb-2">
              Create an Account
            </h2>
            <p className="text-center text-neutral-600 dark:text-neutral-400 mb-8">
              Sign up to start your learning journey
            </p>

            {/* Error */}
            {error && (
              <div className="mb-6 text-red-600 dark:text-red-400 text-sm font-medium text-center bg-red-50/80 dark:bg-red-900/30 border border-red-200/50 dark:border-red-800/50 px-4 py-3 rounded-xl backdrop-blur-sm">
                {error}
              </div>
            )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* First Name & Last Name in single line */}
            <div className="flex gap-4">
              <div className="flex-1">
                <label
                  htmlFor="firstName"
                  className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2"
                >
                  First Name
                </label>
                <div className="relative">
                  <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500 w-4 h-4" />
                  <input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    className="ca-input pl-11"
                    placeholder="First Name"
                  />
                </div>
              </div>

              <div className="flex-1">
                <label
                  htmlFor="lastName"
                  className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2"
                >
                  Last Name
                </label>
                <div className="relative">
                  <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500 w-4 h-4" />
                  <input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    className="ca-input pl-11"
                    placeholder="Last Name"
                  />
                </div>
              </div>
            </div>

            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2"
              >
                Email Address
              </label>
              <div className="relative">
                <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500 w-4 h-4" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="ca-input pl-11"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2"
              >
                Password
              </label>
              <div className="relative">
                <FaLock className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500 w-4 h-4" />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="ca-input pl-11 pr-12"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
                >
                  {showPassword ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
                </button>
              </div>

              {/* Password Rules */}
              <div className="mt-3 p-3 bg-neutral-50/80 dark:bg-white/5 rounded-xl backdrop-blur-sm">
                <ul className="text-sm space-y-1.5">
                  <PasswordRule
                    isValid={passwordRules.upperCase}
                    text="At least one uppercase letter"
                  />
                  <PasswordRule
                    isValid={passwordRules.lowerCase}
                    text="At least one lowercase letter"
                  />
                  <PasswordRule
                    isValid={passwordRules.number}
                    text="At least one number"
                  />
                  <PasswordRule
                    isValid={passwordRules.specialChar}
                    text="At least one special character"
                  />
                  <PasswordRule
                    isValid={passwordRules.minLength}
                    text="Minimum 8 characters"
                  />
                </ul>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2"
              >
                Confirm Password
              </label>
              <div className="relative">
                <FaLock className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500 w-4 h-4" />
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="ca-input pl-11 pr-12"
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
                >
                  {showConfirmPassword ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              className={`w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl font-semibold text-white bg-gradient-to-r ${currentTheme.colors.gradient} hover:opacity-90 shadow-lg ${currentTheme.colors.glow} hover:shadow-xl transition-all duration-300 hover:scale-[1.02]`}
            >
              <FaUserPlus className="w-4 h-4" />
              Sign Up
            </button>
          </form>

          {/* Extra Links */}
          <div className="mt-6 text-center">
            <Link
              className="text-accent-600 dark:text-accent-400 hover:text-accent-700 dark:hover:text-accent-300 font-semibold text-sm transition-colors"
              to="/login"
            >
              Already have an account? Sign In
            </Link>
          </div>
          </div>

          {/* Back to Home */}
          <div className="text-center mt-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 text-sm font-medium transition-colors"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

// ✅ Reusable Rule Component
function PasswordRule({ isValid, text }) {
  return (
    <li
      className={`flex items-center gap-2 transition-colors ${isValid ? "text-emerald-600 dark:text-emerald-400" : "text-neutral-400 dark:text-neutral-500"
        }`}
    >
      {isValid ? <FaCheckCircle className="w-3.5 h-3.5" /> : <FaTimesCircle className="w-3.5 h-3.5" />}
      <span className="text-xs">{text}</span>
    </li>
  );
}
